import React, { type ReactNode, useEffect, useRef, useState } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import {
  ApplicationHeader,
  DEFAULT_SEARCH_SHORTCUT,
} from '@flipdish/ui-library/components/organisms/ApplicationHeader';
import { SidebarNavigation } from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { SidePanel } from '@flipdish/ui-library/components/organisms/SidePanel';
import { useBreakpoints } from '@flipdish/ui-library/hooks/useBreakpoints';
import { useSidePanel } from '@flipdish/ui-library/hooks/useSidePanel';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useHotkeys } from 'react-hotkeys-hook';
import { getTranslate } from 'react-localize-redux';
import { useLocation, useParams } from 'react-router';

import {
  FlipdishAgentPanelContent,
  OPEN_EVENT,
  useFlipdishAssistantEnabled,
} from '../../../components/FlipdishAgent/FlipdishAgentAssistant';
import { useHasAccessToOnboardingV3 } from '../../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import { useSyncCurrentAppWithUrl } from '../../../components/RMS/BrandSelector/hooks/useSyncCurrentAppWithUrl';
import { generalConstants } from '../../../constants/general.constants';
import { flagService } from '../../../services/flagService/flagService';
import NewSidebarPortalStyles from '../NewSidebarPortalStyles';
import type { useSidebarNavItems } from '../useSidebarNavItems';
import { OrganisationPanel } from './components/OrganisationPanel';
import { PortalCommandMenu } from './components/PortalCommandMenu';
import { SwitchOrganisationPanel } from './components/SwitchOrganisationPanel';

const { SCROLLABLE_CONTAINER } = generalConstants;

const tutorialDrawerWidth = 408;

/** Which side panel is open. Notifications are not wired yet. */
type PortalPanel = 'assistant' | 'organisation' | 'switch-organisation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  // 200: above the app's ad-hoc z-indexes (max 101, COG-759), below MUI modal (1300). Remove with MUI.
  sidebarNavLayer: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 200,
    '@media (min-width: 1024px)': {
      '& > .invisible': { display: 'block !important' },
    },
  },
  rightSide: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  headerLayer: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
  },
  // SidePanel contract for a layout of your own: page area and panel are siblings in a fixed-height
  // flex row with `relative` + `overflow: clip`; below `lg` the panel is absolutely positioned in it.
  panelRow: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 0,
    position: 'relative',
    overflow: 'clip',
  },
  content: {
    flexGrow: 1,
    width: '100%',
    minWidth: 0,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    overflowX: 'auto',
    // Page content with its own z-index (sticky table headers) stays under the covering panel.
    isolation: 'isolate',
  },
  switch: {
    flexGrow: 1,
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexDirection: 'column',
  },
  tutorialDrawerOpen: {
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('sm')]: {
      marginRight: tutorialDrawerWidth,
    },
  },
  tutorialDrawerClose: {
    marginRight: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  // Scopes Tailwind for the panel without adding a box between the row and the panel.
  panelLayer: {
    display: 'contents',
  },
  commandMenuLayer: {
    display: 'contents',
  },
}));

type Props = {
  sidebar: ReturnType<typeof useSidebarNavItems>;
  isHelpDrawerOpen?: boolean;
  /** Rendered after the shell row, e.g. the dev toolbar. */
  extras?: ReactNode;
  /** The page: banner and routed content. Rendered inside the scrolling container. */
  children: ReactNode;
};

/**
 * Portal shell behind `showNewApplicationHeader`: SidebarNavigation + ApplicationHeader + one
 * SidePanel (assistant, brand list, organisation switcher) + CommandMenu search, composed by hand so
 * the MUI page stays outside the Tailwind scope. Follows the design system's "Portal Shell" blueprint
 * without AppShell.
 */
export const PortalShell = ({ sidebar, isHelpDrawerOpen, extras, children }: Props) => {
  const classes = useStyles();
  const location = useLocation();
  const { appId } = useParams<{ appId: string }>();
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const currentApp = useAppSelector((state) => state.currentApp);
  const currentOrg = useAppSelector((state) => state.rms.currentOrg);
  const isShowPortalSearchOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showPortalSearch')
  );
  const { reducedOnboardingView, onboardingWaitingView } = useHasAccessToOnboardingV3();
  const isRestrictedOnboardingNav = reducedOnboardingView || onboardingWaitingView;
  const assistantEnabled = useFlipdishAssistantEnabled() && !onboardingWaitingView;
  const isLg = useBreakpoints('lg');

  useSyncCurrentAppWithUrl();

  // Drawer, search and side panel: the three pieces of shell state from the blueprint.
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const sidePanel = useSidePanel<PortalPanel>({ persist: ['assistant'] });
  // A remembered assistant must not reopen while the assistant is gated off (flag off, not yet
  // resolved, or onboarding waiting view). The memory is kept, so it reopens once the gate clears.
  const openPanel = sidePanel.active === 'assistant' && !assistantEnabled ? null : sidePanel.active;
  const isPanelOpen = openPanel !== null;

  // `?orgSearch=` deep link, previously handled by BrandSelector: open the org switcher with it.
  const openPanelAction = sidePanel.open;
  useEffect(() => {
    if (location?.search.includes('orgSearch')) {
      const params = new URLSearchParams(location.search);
      if (params.get('orgSearch')) openPanelAction('switch-organisation');
    }
  }, []);

  const searchEnabled = isShowPortalSearchOn && !isRestrictedOnboardingNav;
  const openSearch = () => setIsSearchOpen(true);
  useHotkeys(DEFAULT_SEARCH_SHORTCUT, openSearch, {
    preventDefault: true,
    enableOnFormTags: true,
    enabled: searchEnabled,
  });

  // Existing callers open the assistant by dispatching OPEN_EVENT on window.
  useEffect(() => {
    if (!assistantEnabled) return;
    const onOpen = () => openPanelAction('assistant');
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [assistantEnabled, openPanelAction]);

  const homeHref = `/${appId ?? currentApp.AppId}/home`;
  const organisationName = currentOrg?.name || currentApp.Name || '';
  const panelLabels: Record<PortalPanel, string> = {
    assistant: translate('Flipdish_Assistant') as string,
    organisation: organisationName,
    'switch-organisation': translate('Switch_organisation') as string,
  };
  // While the panel animates closed it keeps the content it had, so keep its name too.
  const lastPanelLabel = useRef<string>(organisationName);
  if (openPanel) lastPanelLabel.current = panelLabels[openPanel];
  const panelLabel = lastPanelLabel.current;
  const drawerOpen = isHelpDrawerOpen === true;
  const drawerClosed = isHelpDrawerOpen === false;
  // Below `lg` the panel covers the page, which must then be out of reach.
  const pageCovered = isPanelOpen && !isLg;

  return (
    <div className={classes.root} data-fd="portal-shell">
      <NewSidebarPortalStyles />
      <div data-tailwind className={classes.sidebarNavLayer}>
        <SidebarNavigation
          activeUrl={sidebar.activeUrl}
          items={sidebar.items}
          footerItems={sidebar.footerItems}
          account={sidebar.account}
          onNavigate={sidebar.onNavigate}
          showMobileHeader={false}
          mobileMenuOpen={mobileMenuOpen}
          onMobileMenuOpenChange={setMobileMenuOpen}
          homeHref={homeHref}
          {...(searchEnabled
            ? {
                search: {
                  onPress: openSearch,
                  placeholder: translate('Find_a_setting_or_a_page') as string,
                  label: translate('Search') as string,
                },
              }
            : {})}
        />
      </div>
      <div className={classes.rightSide}>
        <div data-tailwind className={classes.headerLayer}>
          <ApplicationHeader
            homeHref={homeHref}
            menu={{ isOpen: mobileMenuOpen, onOpenChange: setMobileMenuOpen }}
            {...(searchEnabled
              ? {
                  search: {
                    onPress: openSearch,
                    placeholder: translate('Find_a_setting_or_a_page') as string,
                    label: translate('Search') as string,
                  },
                }
              : {})}
            {...(assistantEnabled
              ? {
                  assistant: {
                    isOpen: openPanel === 'assistant',
                    onPress: () => sidePanel.toggle('assistant'),
                    label: translate('Flipdish_Assistant') as string,
                  },
                }
              : {})}
            avatar={{
              name: organisationName,
              src: currentApp.LogoImageUrl || undefined,
              onPress: () => sidePanel.toggle('organisation'),
            }}
          />
        </div>
        <div className={classes.panelRow}>
          <main
            className={classes.content}
            data-testid="portal-main-content"
            // React 18 passes the unknown attribute through as `inert=""`. React 19 types it as a
            // boolean, so switch to `inert={pageCovered}` on that upgrade.
            {...(pageCovered ? ({ inert: '' } as Record<string, string>) : {})}
          >
            <div
              className={clsx({
                [classes.switch]: true,
                [classes.tutorialDrawerOpen]: drawerOpen,
                [classes.tutorialDrawerClose]: drawerClosed,
              })}
              id={SCROLLABLE_CONTAINER}
            >
              {children}
            </div>
          </main>
          <div data-tailwind className={classes.panelLayer}>
            <SidePanel
              isOpen={isPanelOpen}
              onOpenChange={sidePanel.onOpenChange}
              aria-label={panelLabel}
            >
              {openPanel === 'assistant' && <FlipdishAgentPanelContent />}
              {openPanel === 'organisation' && (
                <OrganisationPanel
                  title={organisationName}
                  onClose={sidePanel.close}
                  onSwitchOrg={() => openPanelAction('switch-organisation')}
                />
              )}
              {openPanel === 'switch-organisation' && (
                <SwitchOrganisationPanel
                  onDone={sidePanel.close}
                  onBack={() => openPanelAction('organisation')}
                />
              )}
            </SidePanel>
          </div>
        </div>
      </div>
      {searchEnabled && (
        <div data-tailwind className={classes.commandMenuLayer}>
          <PortalCommandMenu isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
        </div>
      )}
      {extras}
    </div>
  );
};

export default PortalShell;
